import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import logo from "../Assets/ABILIO REALTY LOGO 1.svg";
import QuoteModal from "./QuoteModal";

const Navbar = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenQuoteModal = () => {
    setIsQuoteModalOpen(true);
    if (mobileOpen) setMobileOpen(false);
  };

  const handleCloseQuoteModal = () => {
    setIsQuoteModalOpen(false);
  };

  const navLinks = [
    { title: "HOME", path: "/" },
    { title: "ABOUT US", path: null },
    { title: "INTERIOR DESIGN", path: "/interior-design" },
    { title: "CONSTRUCTION", path: "/construction" },
    { title: "REAL ESTATE", path: null },
    { title: "PROJECTS", path: null },
    { title: "CONTACT US", path: null },
  ];

  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        textAlign: "center",
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box sx={{ mb: 4 }}>
        <img
          src={logo}
          alt="Abilio Realty"
          style={{ width: "98px", height: "51px", objectFit: "contain" }}
        />
      </Box>
      <List sx={{ mb: 4 }}>
        {navLinks.map((link) => (
          <ListItem
            key={link.title}
            disablePadding
            {...(link.path ? { component: Link, to: link.path } : {})}
            sx={{ textDecoration: "none", color: "inherit" }}
          >
            <ListItemButton
              sx={{
                textAlign: "center",
                cursor: link.path ? "pointer" : "default",
              }}
            >
              <ListItemText
                primary={link.title}
                primaryTypographyProps={{
                  sx: {
                    fontFamily: "Inter",
                    fontWeight: 600,
                    fontSize: "14px",
                    color:
                      location.pathname === link.path ? "#FF5722" : "#1C1917",
                    letterSpacing: "-0.35px",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Button
        variant="contained"
        onClick={handleOpenQuoteModal}
        sx={{
          background: "linear-gradient(135deg, #B02F00 0%, #FF5722 100%)",
          borderRadius: "8px",
          fontFamily: "Inter",
          fontWeight: 600,
          fontSize: "14px",
          padding: "12px 24px",
          boxShadow: "0px 4px 14px rgba(255, 87, 34, 0.3)",
          textTransform: "none",
        }}
      >
        Get a Quote
      </Button>
    </Box>
  );

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: "#FFFFFF",
        boxShadow: "0px 2px 10px rgba(0,0,0,0.05)",
        height: "80px",
        justifyContent: "center",
        borderBottom: "1px solid #F3F4F6",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "1280px",
          px: "32px",
          position: "relative",
          zIndex: 2,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            height: "80px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {/* Logo Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "98px",
              height: "51px",
              animation: "fadeIn 0.6s ease-out 0.1s backwards",
              "@keyframes fadeIn": {
                "0%": { opacity: 0, transform: "translateY(-10px)" },
                "100%": { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            <img
              src={logo}
              alt="Abilio Realty"
              style={{ width: "98px", height: "51px", objectFit: "contain" }}
            />
          </Box>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              gap: 5,
              animation: "fadeIn 0.6s ease-out 0.1s backwards",
              "@keyframes fadeIn": {
                "0%": { opacity: 0, transform: "translateY(-10px)" },
                "100%": { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            {navLinks.map((link) => (
              <Typography
                key={link.title}
                {...(link.path ? { component: Link, to: link.path } : {})}
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "14px",
                  color:
                    location.pathname === link.path ? "#FF5722" : "#1C1917",
                  lineHeight: "20px",
                  letterSpacing: "-0.35px",
                  cursor: link.path ? "pointer" : "default",
                  transition: "all 0.2s ease",
                  textDecoration: "none",
                  "&:hover": {
                    color: "#FF5722",
                  },
                }}
              >
                {link.title}
              </Typography>
            ))}
          </Box>

          {/* Action Button Section */}
          <Box
            sx={{
              display: { xs: "none", lg: "block" },
              animation: "fadeIn 0.6s ease-out 0.2s backwards",
              "@keyframes fadeIn": {
                "0%": { opacity: 0, transform: "translateY(-10px)" },
                "100%": { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            <Button
              variant="contained"
              onClick={handleOpenQuoteModal}
              sx={{
                background: "linear-gradient(135deg, #B02F00 0%, #FF5722 100%)",
                borderRadius: "4px", // Modern slightly rounded corners
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "-0.35px",
                padding: "10px 24px",
                boxShadow: "none",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #FF5722 0%, #B02F00 100%)",
                  transform: "translateY(-1px)",
                  boxShadow: "0px 4px 12px rgba(176, 47, 0, 0.2)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                textTransform: "none",
              }}
            >
              Get a Quote
            </Button>
          </Box>

          {/* Mobile Menu Icon */}
          <Box sx={{ display: { xs: "flex", lg: "none" } }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleDrawerToggle}
              sx={{ color: "#57534E" }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Sidebar - Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }} // Better open performance on mobile
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: "280px",
            backgroundColor: "#FFFFFF",
          },
        }}
      >
        {drawer}
      </Drawer>
      <QuoteModal open={isQuoteModalOpen} onClose={handleCloseQuoteModal} />
    </AppBar>
  );
};

export default Navbar;
